import { useEffect, useMemo } from 'react';

// Import store
import { useScheduleStore } from '../../store/scheduleStore';

// Import utils
import { getAllUniqueTimezones, findTimezoneByOffset } from '../../utils/date';

// Import hooks
import useLocalTimezone from './useLocalTimezone';

/**
 * Manages timezone selection and 12h/24h hour mode.
 * Mirrors the previous inline logic in the SchedulePage for initial timezone calculation.
 */
export function useTimezoneMode() {
  const timezone = useScheduleStore((s) => s.timezone);
  const setTimezone = useScheduleStore((s) => s.setTimezone);
  const hourMode = useScheduleStore((s) => s.hourMode);
  const setHourMode = useScheduleStore((s) => s.setHourMode);
  const initialTZ = useScheduleStore((s) => s.initialTZ);
  const setInitialTZ = useScheduleStore((s) => s.setInitialTZ);

  const currentTZ = useLocalTimezone();

  const computedInitialTZ = useMemo(() => {
    const localTZ = currentTZ;
    const allTimezones = getAllUniqueTimezones();
    
    // If the local timezone is in the list, use it
    if (allTimezones.indexOf(localTZ) !== -1) {
      return localTZ;
    }
    
    // Otherwise, try to find a timezone with the same offset
    const matchingTZ = findTimezoneByOffset(localTZ);
    if (matchingTZ) {
      return matchingTZ;
    }
    
    // Fall back to UTC if no match found
    return 'UTC';
  }, [currentTZ]);

  // Initialize the initialTZ and default timezone if not yet set
  useEffect(() => {
    if (!initialTZ || initialTZ === 'UTC') {
      setInitialTZ(computedInitialTZ);
      if (!timezone || timezone === 'UTC') setTimezone(computedInitialTZ);
    }
  }, [computedInitialTZ, initialTZ, setInitialTZ, setTimezone, timezone]);

  const resetTimezone = () => {
    setTimezone(initialTZ || computedInitialTZ);
  };

  return {
    timezone,
    setTimezone,
    hourMode,
    setHourMode,
    initialTZ: initialTZ || computedInitialTZ,
    resetTimezone,
  };
}

export default useTimezoneMode;
